import type { HttpClient } from '@shared/api/httpClient';
import { canDownloadClientArtifacts } from '@shared/auth/cognito';
import type { ApiRecord } from './packageUi';
import { canClientDownloadPackage, packageId } from './packageUi';

export type PackageDownloadStatus = {
  packageId?: string;
  state: 'idle' | 'preparing' | 'error';
  message?: string;
};

export type PackageDownloadUrlResponse = {
  downloadUrl: string;
  method?: 'GET' | string;
  expiresInSeconds?: number;
  packageId?: string;
  packageName?: string;
  fileName?: string;
};

export async function requestPackageDownloadUrl(
  api: HttpClient,
  pkg: ApiRecord,
): Promise<PackageDownloadUrlResponse> {
  const id = packageId(pkg);
  if (!id) {
    throw new Error('Package ID is required to generate a download link.');
  }
  if (!canDownloadClientArtifacts()) {
    throw new Error('Downloads are available only to client administrators. Your current role can still review package status and details.');
  }
  if (!canClientDownloadPackage(pkg)) {
    throw new Error('Client admins can download final auditor evidence packages only. Open the evidence workspace to review technical evidence package contents.');
  }

  const response = await api.post<PackageDownloadUrlResponse>(
    `/client/packages/${encodeURIComponent(id)}/download-url`,
    {},
  );

  if (!response.downloadUrl) {
    throw new Error('The API did not return a download URL.');
  }

  return response;
}

function openDownloadUrlOnce(downloadUrl: string): void {
  // Use a single browser navigation path for downloads.
  // window.open(..., 'noopener,noreferrer') can return null even when the
  // browser successfully starts the download, which makes fallback navigation
  // unsafe because it can trigger the same presigned URL a second time.
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function openPackageDownload(api: HttpClient, pkg: ApiRecord): Promise<void> {
  const response = await requestPackageDownloadUrl(api, pkg);
  openDownloadUrlOnce(response.downloadUrl);
}
